import {
  ArticleListItemFields,
  GenericItemListFields,
  ItemListingCardModel,
  ItemListingContentType,
  ItemListingSitecoreItem,
  StudyListItemFields,
} from './ItemListing.type';

const DEFAULT_ARTICLE_LINK_TEXT = 'Learn more';

function getFieldString(field?: { value?: unknown } | string | null): string {
  if (field === undefined || field === null) return '';
  if (typeof field === 'string') return field;
  if (field.value === undefined || field.value === null) return '';
  return String(field.value);
}

function getImageSrc(imageField?: { value?: { src?: string; alt?: string } | string }): string {
  const value = imageField?.value;
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.src || '';
}

function getImageAlt(imageField?: { value?: { src?: string; alt?: string } | string }): string {
  const value = imageField?.value;
  if (!value || typeof value === 'string') return '';
  return value.alt || '';
}

function getItemId(item: ItemListingSitecoreItem, fallbackTitle: string): string {
  return item.id || item.url || item.name || fallbackTitle;
}

type RelatedTrialObject = Exclude<NonNullable<StudyListItemFields['relatedTrial']>, string>;

function getRelatedTrialFields(
  fields: StudyListItemFields
): RelatedTrialObject['fields'] | undefined {
  const related = fields.relatedTrial;
  if (!related || typeof related === 'string') return undefined;
  return related.fields;
}

export function mapStudyListItem(item: ItemListingSitecoreItem): ItemListingCardModel | null {
  const fields = (item.fields || {}) as StudyListItemFields;
  const related = getRelatedTrialFields(fields);

  const title = getFieldString(related?.trialTitle) || item.displayName || item.name || '';
  if (!title) return null;

  return {
    id: getItemId(item, title),
    condition: getFieldString(related?.featuredTrialCondition?.fields?.description),
    title,
    description: getFieldString(related?.trialDescription),
    imageSrc: getImageSrc(related?.featuredTrialImage),
    imageAlt: getImageAlt(related?.featuredTrialImage) || title,
    linkHref: fields.trialCTA?.value?.href || item.url || '',
    linkText: fields.trialCTA?.value?.text || DEFAULT_ARTICLE_LINK_TEXT,
  };
}

export function mapArticleListItem(item: ItemListingSitecoreItem): ItemListingCardModel | null {
  const fields = (item.fields || {}) as ArticleListItemFields;
  const title = getFieldString(fields.articleTitle);
  if (!title) return null;

  return {
    id: getItemId(item, title),
    condition: getFieldString(fields.articleConditions?.fields?.description),
    title,
    description: getFieldString(fields.articleDescription),
    imageSrc: getImageSrc(fields.articleImage),
    imageAlt: getImageAlt(fields.articleImage) || title,
    linkHref: item.url || '',
    linkText: '',
  };
}

/** Stub for future itemList pages — not wired in ItemListing parent yet. */
export function mapGenericItemListItem(item: ItemListingSitecoreItem): ItemListingCardModel | null {
  const fields = (item.fields || {}) as GenericItemListFields;
  const title = getFieldString(fields.title);
  if (!title) return null;

  return {
    id: getItemId(item, title),
    condition: '',
    title,
    description: getFieldString(fields.description),
    imageSrc: getImageSrc(fields.image),
    imageAlt: getImageAlt(fields.image) || title,
    linkHref: fields.button?.value?.href || '',
    linkText: fields.button?.value?.text || '',
  };
}

export function mapItemListingCards(
  items: ItemListingSitecoreItem[] | undefined,
  contentType: ItemListingContentType
): ItemListingCardModel[] {
  if (!items?.length) return [];

  return items
    .map((item) => {
      if (contentType === ItemListingContentType.Study) return mapStudyListItem(item);
      if (contentType === ItemListingContentType.Article) return mapArticleListItem(item);
      return mapGenericItemListItem(item);
    })
    .filter((card): card is ItemListingCardModel => Boolean(card));
}

/**
 * Resolve which list to show.
 * Prefer Sitecore rendering param `list` (Droplist: "Study Listing" | "Article Listing"),
 * then fall back to the page path (view-all-studies / view-all-articles).
 */
export function resolveItemListingContentType(
  asPath: string,
  listParam?: string
): Exclude<ItemListingContentType, 'item'> | null {
  const fromParam = resolveContentTypeFromListParam(listParam);
  if (fromParam) return fromParam;

  const path = (asPath || '').split('?')[0].split('#')[0].toLowerCase();
  if (path.includes('view-all-studies')) return ItemListingContentType.Study;
  if (path.includes('view-all-articles')) return ItemListingContentType.Article;
  return null;
}

export function resolveContentTypeFromListParam(
  listParam?: string
): Exclude<ItemListingContentType, 'item'> | null {
  if (!listParam) return null;
  const normalized = listParam.toLowerCase().replace(/[\s_-]/g, '');
  if (normalized.includes('study')) return ItemListingContentType.Study;
  if (normalized.includes('article')) return ItemListingContentType.Article;
  return null;
}
